// src/utils/storage.js
// 存储工具 - 用于保存和获取用户设置

// 不同的存储键名，支持不同页面或全局设置
const STORAGE_KEYS = {
    GLOBAL: 'global_background_settings',
    USER_MANAGEMENT: 'user_management_background_settings',
    COACH_MANAGEMENT: 'coach_management_background_settings',
    MY_RESERVATIONS: 'my_reservations_background_settings',
    RESERVATION: 'reservation_background_settings',
    COACH_RESERVATIONS: 'coach_reservations_background_settings',
    SCHEDULE: 'schedule_background_settings'
}

// 背景图片预设
export const bgPresets = [
    {
        name: '科幻',
        url: 'https://images8.alphacoders.com/135/thumb-1920-1352905.png'
    },
    {
        name: '自然风景',
        url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'
    },
    {
        name: '郊区夜景',
        url: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'
    },
    {
        name: '抽象艺术',
        url: 'https://images.unsplash.com/photo-1541701494587-cb58502866ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'
    },
    {
        name: '天空云彩',
        url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'
    },
    {
        name: '雪山',
        url: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80'
    }
]

// 默认背景设置
export const defaultBackgroundSettings = {
    type: 'image',
    imageUrl: bgPresets[0].url,
    blurLevel: 5,
    opacity: 0.8,
    backgroundColor: '#f0f2f5',
    applyGlobally: false,
    customImage: null
}

// 获取所有页面键名
export const ALL_PAGE_KEYS = Object.values(STORAGE_KEYS)

// 获取全局背景设置
export function getGlobalBackgroundSettings() {
    return getBackgroundSettings(STORAGE_KEYS.GLOBAL)
}

// 保存全局背景设置
export function saveGlobalBackgroundSettings(settings) {
    return saveBackgroundSettings(STORAGE_KEYS.GLOBAL, settings)
}

// 获取指定页面的背景设置
export function getPageBackgroundSettings(pageKey) {
    // 先获取全局设置
    const globalSettings = getGlobalBackgroundSettings()

    // 获取页面特定设置
    const pageSettings = getBackgroundSettings(STORAGE_KEYS[pageKey.toUpperCase()] || STORAGE_KEYS.GLOBAL)

    // 如果页面设置存在，使用页面设置
    if (pageSettings) {
        return pageSettings
    }

    // 如果全局设置存在且页面允许使用全局，使用全局设置
    if (globalSettings && globalSettings.applyGlobally) {
        return globalSettings
    }

    return null
}

// 保存指定页面的背景设置
export function savePageBackgroundSettings(pageKey, settings) {
    const key = STORAGE_KEYS[pageKey.toUpperCase()] || STORAGE_KEYS.GLOBAL
    return saveBackgroundSettings(key, settings)
}

// 通用获取背景设置
export function getBackgroundSettings(key = STORAGE_KEYS.GLOBAL) {
    try {
        const settings = localStorage.getItem(key)
        if (settings) {
            const parsed = JSON.parse(settings)
            // 兼容旧版本设置
            return migrateSettings(parsed)
        }
        return null
    } catch (error) {
        console.error('读取背景设置失败:', error)
        return null
    }
}

// 通用保存背景设置
export function saveBackgroundSettings(key, settings) {
    try {
        localStorage.setItem(key, JSON.stringify(settings))
        return true
    } catch (error) {
        console.error('保存背景设置失败:', error)
        return false
    }
}

// 清除背景设置
export function clearBackgroundSettings(key) {
    try {
        if (key) {
            localStorage.removeItem(key)
        } else {
            // 清除所有背景设置
            ALL_PAGE_KEYS.forEach(k => {
                localStorage.removeItem(k)
            })
        }
        return true
    } catch (error) {
        console.error('清除背景设置失败:', error)
        return false
    }
}

// 检查是否有旧的设置并迁移到新的
export function migrateOldSettings() {
    try {
        const oldKeys = ['reservation_background_settings', 'background_settings']

        oldKeys.forEach(oldKey => {
            const oldSettings = localStorage.getItem(oldKey)

            if (oldSettings) {
                try {
                    const parsed = JSON.parse(oldSettings)

                    // 迁移到全局设置（如果全局设置不存在）
                    const globalSettings = getGlobalBackgroundSettings()
                    if (!globalSettings) {
                        saveGlobalBackgroundSettings(migrateSettings(parsed))
                    }

                    // 删除旧设置（可选）
                    // localStorage.removeItem(oldKey)

                    console.log(`已迁移旧设置: ${oldKey}`)
                } catch (e) {
                    console.error(`迁移设置 ${oldKey} 失败:`, e)
                }
            }
        })

        return true
    } catch (error) {
        console.error('迁移设置失败:', error)
        return false
    }
}

// 迁移旧版设置到新版格式
function migrateSettings(settings) {
    if (!settings) return null

    // 如果已经是新版格式，直接返回
    if (settings.type && settings.blurLevel !== undefined) {
        return settings
    }

    // 旧版迁移到新版
    const migrated = { ...defaultBackgroundSettings }

    if (typeof settings === 'string') {
        // 如果是纯字符串，可能是图片URL
        migrated.type = 'image'
        migrated.imageUrl = settings
    } else if (settings.imageUrl) {
        // 旧版对象格式
        migrated.type = settings.type || 'image'
        migrated.imageUrl = settings.imageUrl
        migrated.blurLevel = settings.blur || settings.blurLevel || 5
        migrated.opacity = settings.opacity || 0.8
        migrated.backgroundColor = settings.backgroundColor || '#f0f2f5'
    }

    return migrated
}

// 导出所有页面的设置（用于管理界面）
export function getAllBackgroundSettings() {
    const allSettings = {}

    Object.keys(STORAGE_KEYS).forEach(key => {
        allSettings[key.toLowerCase()] = getBackgroundSettings(STORAGE_KEYS[key])
    })

    return allSettings
}

// 批量保存设置
export function saveAllBackgroundSettings(settingsMap) {
    try {
        Object.keys(settingsMap).forEach(key => {
            const storageKey = STORAGE_KEYS[key.toUpperCase()]
            if (storageKey && settingsMap[key]) {
                saveBackgroundSettings(storageKey, settingsMap[key])
            }
        })
        return true
    } catch (error) {
        console.error('批量保存背景设置失败:', error)
        return false
    }
}

// 应用设置到所有页面
export function applySettingsToAllPages(settings) {
    try {
        const allKeys = Object.values(STORAGE_KEYS)
        const settingsToSave = {
            ...settings,
            applyGlobally: true
        }

        // 保存到所有页面
        allKeys.forEach(key => {
            saveBackgroundSettings(key, settingsToSave)
        })

        // 保存到全局设置
        saveGlobalBackgroundSettings(settingsToSave)

        console.log('设置已应用到所有页面')
        return true
    } catch (error) {
        console.error('应用到所有页面失败:', error)
        return false
    }
}

// 检查是否为全局应用
export function isGlobalApplied() {
    const globalSettings = getGlobalBackgroundSettings()
    return globalSettings && globalSettings.applyGlobally === true
}

// 预加载背景图片
export function preloadBackgroundImages() {
    try {
        const settings = getGlobalBackgroundSettings()
        if (settings && settings.type === 'image' && settings.imageUrl) {
            const img = new Image()
            img.src = settings.imageUrl
            img.onload = () => {
                console.log('背景图片预加载成功')
            }
            img.onerror = () => {
                console.warn('背景图片预加载失败，使用默认背景')
            }
        }
    } catch (error) {
        console.error('预加载背景图片失败:', error)
    }
}

// 检查存储空间
export function checkStorageSpace() {
    try {
        const testKey = '__storage_test__'
        const testValue = 'x'.repeat(1024 * 1024) // 1MB 测试数据

        localStorage.setItem(testKey, testValue)
        localStorage.removeItem(testKey)

        return true
    } catch (error) {
        console.warn('本地存储空间可能已满')
        return false
    }
}

// 获取所有页面键（不包括全局键）
export function getAllPageKeys() {
    const keys = Object.keys(STORAGE_KEYS)
    // 过滤掉全局键
    return keys.filter(key => key !== 'GLOBAL').map(key => STORAGE_KEYS[key])
}

// 导出存储键常量
export const STORAGE_KEY_MAP = STORAGE_KEYS
